import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Typography from "@mui/material/Typography"
import { useState } from "react"

import MuiAutoComplete from "components/MuiAutocomplete"

type Props = {
    onClose: () => void
    isFetchingChargerTemplate: boolean
    isFetchingMaterialTemplate: boolean
    onDownloadTemplate: (type: "charger" | "material") => void
    onParseChargersFile: (files: FileList) => void
    onParseMaterialsFile: (files: FileList) => void
}

const TYPE_OPTIONS = [
    { id: "charger" as const, title: "Charger" },
    { id: "material" as const, title: "Non Charger" },
]

const UploadFileButton = ({ onChange }: { onChange: (files: FileList) => void }) => (
    <Button component="label" variant="contained" color="statiqOrange" tabIndex={-1} sx={{ height: "40px" }}>
        Upload File
        <input
            type="file"
            hidden
            accept=".xlsx, .xls, .xlsm"
            onChange={e => {
                if (e.target.files?.length) onChange(e.target.files)
                e.target.value = ""
            }}
        />
    </Button>
)

/**
 * The Type selector picks which of the two real upload endpoints (parse_chargers/parse_materials,
 * each with its own template — chargers also carry branding and power rating) the Download
 * Template / Upload File row drives, instead of showing both always.
 */
const BulkUploadPanel = ({
    onClose,
    isFetchingChargerTemplate,
    isFetchingMaterialTemplate,
    onDownloadTemplate,
    onParseChargersFile,
    onParseMaterialsFile,
}: Props) => {
    const [type, setType] = useState(TYPE_OPTIONS[0])
    const isCharger = type.id === "charger"

    return (
        <Box display="flex" flexDirection="column" gap={2} padding={2} border="1px dashed #B7BAC6" borderRadius="8px">
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box display="flex" flexDirection="column" gap={0.5}>
                    <Typography fontSize="14px" fontWeight={600}>
                        Bulk upload items
                    </Typography>
                    <Typography fontSize="11px" color="#70758D">
                        Columns: SKU Code, Quantity{isCharger ? ", Branding, Power Rating" : ""}. Excel (.xlsx,
                        .xls, .xlsm).
                    </Typography>
                </Box>
                <Typography
                    variant="label"
                    fontSize="12px"
                    fontWeight={400}
                    textTransform="none"
                    onClick={onClose}
                    sx={{
                        textDecoration: "underline",
                        textUnderlineOffset: "2px",
                        color: "#0430C0",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                    }}>
                    Close
                </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <Box width="150px">
                    <MuiAutoComplete
                        disablePortal={false}
                        disableClearable
                        size="small"
                        options={TYPE_OPTIONS}
                        value={type}
                        onChange={(value: any) => setType(value || TYPE_OPTIONS[0])}
                    />
                </Box>
                <Button
                    variant="outlined"
                    onClick={() => onDownloadTemplate(type.id)}
                    loading={isCharger ? isFetchingChargerTemplate : isFetchingMaterialTemplate}>
                    Download Template
                </Button>
                <UploadFileButton onChange={isCharger ? onParseChargersFile : onParseMaterialsFile} />
            </Box>
        </Box>
    )
}

export default BulkUploadPanel
